import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { errorMessage } from '@/api/errorMessage';
import type { LeadInput } from '@/api/types';
import type { BrandState } from '@/marketplace/brands';
import { schemeFor } from '@/marketplace/format';
import { useTenant } from '@/tenant/useTenant';
import { useSendLead } from '../api';

interface LeadFormProps {
  brand: BrandState;
  /** Domain chosen in the domain lab (null = the included address). */
  domain: string | null;
}

/** "I want to know more": saved for the platform team, who answers on WhatsApp. */
export function LeadForm({ brand, domain }: LeadFormProps) {
  const send = useSendLead();
  const { config } = useTenant();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [profession, setProfession] = useState<LeadInput['profession']>('personal');
  const [students, setStudents] = useState('');
  const [city, setCity] = useState('');
  const [message, setMessage] = useState('');
  const [wantsDomain, setWantsDomain] = useState<boolean | null>(null);
  const [desired, setDesired] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // What the domain lab chose, until the person changes it here.
  const wants = wantsDomain ?? Boolean(domain);
  const desiredDomain = desired ?? domain ?? '';
  const valid =
    name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    whatsapp.replace(/\D/g, '').length >= 10;
  const accent = schemeFor(brand.color).light.primary.main;

  const submit = async () => {
    await send.mutateAsync({
      name: name.trim(),
      email: email.trim(),
      whatsapp,
      profession,
      students: (students || null) as LeadInput['students'],
      city: city.trim() || null,
      wantsCustomDomain: wants,
      desiredDomain: wants ? desiredDomain.trim() || null : null,
      brandName: brand.name.trim() || null,
      brandColor: brand.color,
      message: message.trim() || null,
    });
    setDone(true);
  };

  if (done) {
    return (
      <Card sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
        <CheckCircleRounded color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5" sx={{ mt: 1 }}>
          Recebemos! Em breve falamos com você pelo WhatsApp.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Já anotamos a sua marca{brand.name ? `, ${brand.name},` : ''} e a cor que você escolheu
          {wants && desiredDomain ? `, e o interesse no domínio ${desiredDomain}` : ''}.
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: { xs: 2.5, md: 4 } }}>
      <Stack spacing={2.5}>
        {send.isError && (
          <Alert severity="error">{errorMessage(send.error, 'Não foi possível enviar.')}</Alert>
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Você é
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={profession}
            onChange={(_, value: LeadInput['profession'] | null) => value && setProfession(value)}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="personal">Personal trainer</ToggleButton>
            <ToggleButton value="nutricionista">Nutricionista</ToggleButton>
            <ToggleButton value="estudio">Estúdio ou equipe</ToggleButton>
            <ToggleButton value="outro">Outro</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: 120 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="WhatsApp"
              placeholder="(11) 98765-4321"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              fullWidth
              required
              slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 20 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: 320 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              label="Alunos hoje"
              value={students}
              onChange={(e) => setStudents(e.target.value)}
              fullWidth
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">Prefiro não dizer</MenuItem>
              <MenuItem value="ate-10">Até 10</MenuItem>
              <MenuItem value="11-30">11 a 30</MenuItem>
              <MenuItem value="31-80">31 a 80</MenuItem>
              <MenuItem value="mais-80">Mais de 80</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Cidade"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 80 } }}
            />
          </Grid>
        </Grid>
        <Box
          sx={(theme) => ({
            p: 2,
            borderRadius: 2,
            border: `1px dashed ${theme.vars.palette.divider}`,
          })}
        >
          <FormControlLabel
            control={<Switch checked={wants} onChange={(e) => setWantsDomain(e.target.checked)} />}
            label="Quero um domínio próprio (serviço à parte)"
          />
          {wants && (
            <TextField
              size="small"
              label="Domínio desejado"
              placeholder="anapersonal.app"
              value={desiredDomain}
              onChange={(e) => setDesired(e.target.value.toLowerCase().trim())}
              fullWidth
              sx={{ mt: 1 }}
              slotProps={{ htmlInput: { maxLength: 253 } }}
            />
          )}
        </Box>
        <TextField
          label="Quer contar algo? (opcional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Button
            variant="contained"
            size="large"
            disabled={!valid || send.isPending}
            onClick={() => void submit().catch(() => undefined)}
          >
            {send.isPending ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              `Quero conhecer a ${config.platformName}`
            )}
          </Button>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }}
            />
            <Typography variant="caption" color="text.secondary">
              Vamos junto: {brand.name || 'sua marca'} nesta cor, para a demonstração.
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Usamos seus dados só para falar com você sobre a {config.platformName}. Nada de spam.
        </Typography>
      </Stack>
    </Card>
  );
}
